import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import request from "../../../Helpers/API";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { SaveIcon } from "../Helper/Icons";
import { Select } from "../Helper/Select";
import { LoaderScreen } from "../../components/LoaderScreen";

const BuildingIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 3v18M3 9h6M3 15h6M15 9h6M15 15h6" />
    </svg>
);

/**
 * EncargadosSucursalModal
 *
 * Props:
 *   open        — bool
 *   onClose     — fn()
 *   sucursales  — [{ idsucursal, sucursal }]
 *   responsable — [{ idsucursal, idusuario, usuario }]  ← encargados actuales
 *   empleados   — [{ idusuario, usuario, idsucursal }]               ← opciones para cambiar
 *   onActualizar — fn()
 */
export const EncargadosSucursalModal = ({
    open,
    onClose,
    sucursales = [],
    responsable = [],
    empleados = [],
    onActualizar,
}) => {
    // Estado local: mapa de idsucursal → idusuario seleccionado
    const [asignaciones, setAsignaciones] = useState({});
    const [loading, setLoading] = useState(false);

    // Inicializar asignaciones con los responsables actuales al abrir
    useEffect(() => {
        if (open) {
            const inicial = {};
            sucursales.forEach(suc => {
                const enc = responsable.find(r => r.idsucursal === suc.idsucursal);
                inicial[suc.idsucursal] = enc?.idusuario ?? "";
            });
            setAsignaciones(inicial);
        }
    }, [open, sucursales, responsable]);

    if (!open) return null;

    const opcionesEmpleados = empleados.map(e => ({
        value: e.idusuario,
        label: e.usuario,
    }));

    const handleChange = (idsucursal, idusuario) => {
        setAsignaciones(prev => ({ ...prev, [idsucursal]: idusuario }));
    };

    const handleGuardar = () => {
        // Validar que todas las sucursales tengan encargado
        const sinAsignar = sucursales.filter(s => !asignaciones[s.idsucursal]);
        if (sinAsignar.length > 0) {
            const nombres = sinAsignar.map(s => s.sucursal).join(", ");
            Swal.fire(
                "ENCARGADOS DE SUCURSAL",
                `Las siguientes sucursales no tienen encargado asignado: ${nombres}`,
                "warning"
            );
            return;
        }

        setLoading(true);

        // Construir array de asignaciones para el API
        const post = {
            encargados: JSON.stringify(
                sucursales.map(suc => ({
                    idsucursal: suc.idsucursal,
                    idusuario: asignaciones[suc.idsucursal],
                }))
            )
        };

        request
            .post("ProgramarInventario/GuardarEncargados", post)
            .then(async (result) => {
                setLoading(false);
                if (result.resultado === 1) {
                    await Swal.fire("ENCARGADOS DE SUCURSAL", result.mensaje, "error");
                } else {
                    await Swal.fire("ENCARGADOS DE SUCURSAL", "Encargados actualizados con éxito", "success");
                    onActualizar?.();
                    onClose?.();
                }
            })
            .catch((error) => {
                setLoading(false);
                Swal.fire("ENCARGADOS DE SUCURSAL", JSON.stringify(error), "error");
            });
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose?.();
    };

    return (
        <div className="ram-backdrop ram-backdrop--secondary" onClick={handleBackdropClick}>
            <PlantillaScreen
                titulo={"Encargados de sucursal"}
                className={"ram-modal esm-modal"}
            >
                <button className="ram-close" onClick={onClose} type="button">
                    X
                </button>

                <LoaderScreen loader={loading} />

                {/* ── Body ── */}
                <div className="ram-body esm-body">

                    {sucursales.length === 0 ? (
                        <div className="esm-empty">Sin sucursales disponibles.</div>
                    ) : (
                        sucursales.map(suc => {
                            const encActual = responsable.find(r => r.idsucursal === suc.idsucursal);
                            const tieneEncargado = !!asignaciones[suc.idsucursal];

                            // Filtrar empleados que pertenecen a esta sucursal
                            const opcionesSucursal = empleados
                                .filter(e => e.idsucursal === suc.idsucursal)
                                .map(e => ({ value: e.idusuario, label: e.usuario }));

                            return (
                                <div key={suc.idsucursal} className="esm-row">
                                    {/* Ícono + nombre sucursal */}
                                    <div className="esm-row__sucursal">
                                        <div className={`esm-row__icon ${tieneEncargado ? "" : "esm-row__icon--empty"}`}>
                                            <BuildingIcon />
                                        </div>
                                        <div className="esm-row__info">
                                            <span className="esm-row__nombre">{suc.sucursal}</span>
                                            <span className="esm-row__sub">
                                                {encActual
                                                    ? `Actual: ${encActual.usuario}`
                                                    : "Sin encargado asignado"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Select de empleados */}
                                    <div className="esm-row__select">
                                        <Select
                                            label=""
                                            options={opcionesSucursal}
                                            value={asignaciones[suc.idsucursal] ?? ""}
                                            onChange={(val) => handleChange(suc.idsucursal, val)}
                                            placeholder="Seleccione encargado"
                                        />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="ram-footer">
                    <button
                        type="button"
                        className="ram-btn ram-btn--primary"
                        onClick={handleGuardar}
                        disabled={loading || sucursales.length === 0}
                    >
                        {loading ? <span className="ram-btn__spinner" /> : <SaveIcon />}
                        {loading ? "Guardando..." : "Guardar"}
                    </button>
                </div>

            </PlantillaScreen>
        </div>
    );
};

export default EncargadosSucursalModal;
